import React from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import PreviewTimezonePicker from './PreviewTimezonePicker';
import type { SubmitScheduleModel } from './SubmitScheduleModel';

interface SchedulePreviewPanelProps {
  readonly schedule: SubmitScheduleModel;
  /** moment token for the time part, per the 12h/24h preference. */
  readonly displayTimeToken: string;
}

/**
 * What the submit will actually do.
 *
 * Exists because two things are invisible on the form itself: a closed
 * scheduling window silently pushes the start later, and a recurrence's
 * start_time applies only from the second occurrence — so the gap between the
 * first two runs can differ from the rule's interval. Both are surfaced here
 * rather than corrected behind the operator's back.
 *
 * Nothing here is live. The answer is anchored to the instant it was calculated,
 * so that instant is stated and refreshing is the operator's deliberate act —
 * see {@link SubmitScheduleModel.previewAsOf}.
 */
const SchedulePreviewPanel: React.FC<SchedulePreviewPanelProps> = observer(({ schedule, displayTimeToken }) => {
  const { preview } = schedule;
  const timezone = schedule.previewTimezone;

  /**
   * Renders one of the panel's datetimes. Every value reaching this is an
   * absolute instant, so the only decision left is which zone to read it in —
   * which makes the rendering a pure presentation concern that cannot alter the
   * value.
   *
   * Not `<FormatDateTime>`. That component consults `sourceTimezone` only while
   * the global "use local timezone" preference is off, so a zone chosen for this
   * panel would silently lose to a global display preference. It also cannot emit
   * a zone abbreviation, which is load-bearing here: these are times the operator
   * is about to commit to in a zone they chose, so "00:01 CEST" and a bare
   * "00:01" are not the same claim.
   */
  const format = (value: Date): string => moment(value).tz(timezone).format(`YYYY-MM-DD ${displayTimeToken} z`);

  const pending = <Spinner animation="border" size="sm" role="status" aria-label="Checking the schedule" />;

  // Reads the preview from another angle — the window's zone, a rule's zone, the
  // operator's own. Sits inside the panel rather than beside it because the
  // submit timezone select is a few lines below on the same form, and these two
  // must not read as the same choice: this one moves nothing but the display.
  //
  // Takes the selection AND the resolved zone: the trigger displays the latter
  // (what the times are actually in), while the former is what "chosen" means —
  // null is a real state here, and collapsing the two is what made an earlier
  // combobox binding snap back to the default mid-keystroke.
  const timezoneControl = (
    <PreviewTimezonePicker value={schedule.previewTimezoneSelection} resolved={schedule.previewTimezone} onChange={schedule.handlePreviewTimezoneChange} />
  );

  // Through `format` like every other datetime here, and for one reason of its
  // own: this value exists to be weighed against the start it precedes, and an
  // age stated in one zone against a start in another cannot be compared at a
  // glance.
  const asOf = schedule.previewAsOf;
  const asOfLine = asOf && (
    <div className="text-muted small d-flex align-items-center gap-2 mt-2">
      <span>Checked at {format(asOf)}</span>
      <Button variant="link" size="sm" className="p-0 lh-1 text-decoration-none" onClick={() => void schedule.refreshNow()} disabled={schedule.isPreviewStale}>
        <FontAwesomeIcon icon={faArrowsRotate} className="fs-10 me-1" />
        Refresh
      </Button>
    </div>
  );

  // A failed result must not be left on screen while the next attempt is in
  // flight. The slow case is exactly the failing one — a window with no opening
  // makes the backend rescan two years before giving up — so a stale error with
  // no pending indicator is precisely when this reads as a hang.
  if (schedule.isPreviewStale && preview.kind !== 'ready') {
    return (
      <div className="text-muted small d-flex align-items-center gap-2">
        {pending}
        Checking the schedule…
      </div>
    );
  }

  if (preview.kind === 'unschedulable') {
    return (
      <Alert variant="outline-danger" className="mb-0">
        <div className="fw-semibold">Cannot be scheduled</div>
        <div className="small">{preview.message}</div>
        {asOfLine}
      </Alert>
    );
  }

  if (preview.kind === 'error') {
    return (
      <Alert variant="outline-warning" className="mb-0">
        <div className="fw-semibold">Could not determine the start</div>
        <div className="small">{preview.message}</div>
        {asOfLine}
      </Alert>
    );
  }

  return (
    <div>
      <div className="text-muted small d-flex align-items-center gap-2">
        Starts
        {/* A known-outdated value is greyed with the spinner rather than hidden,
            so the panel does not flicker on every keystroke. */}
        {schedule.isPreviewStale && pending}
      </div>
      {schedule.startsOnSubmit ? (
        <div className="fw-semibold">Immediately, when you submit</div>
      ) : (
        <div className={`fw-semibold ${schedule.isPreviewStale ? 'text-muted' : ''}`}>{schedule.startsAt && format(schedule.startsAt)}</div>
      )}

      {preview.postponed && (
        <div className="text-muted small mt-1">
          Moved from {format(preview.postponed.from)} — scheduling window <code>{preview.postponed.byWindow}</code> was closed.
        </div>
      )}

      {preview.occurrences.length > 0 && (
        <>
          <div className="text-muted small mt-3">Then</div>
          <ul className="list-unstyled mb-0 small">
            {preview.occurrences.map((occurrence) => (
              <li key={occurrence.toISOString()}>{format(occurrence)}</li>
            ))}
          </ul>
        </>
      )}

      {/* Only where a datetime is actually rendered. Under `startsOnSubmit` the
          panel states no time at all, so a zone to read one in means nothing —
          unless the following occurrences are listed, which do carry times. */}
      {(!schedule.startsOnSubmit || preview.occurrences.length > 0) && timezoneControl}

      {asOfLine}
    </div>
  );
});

export default SchedulePreviewPanel;
